import { JSONValue } from '../../../shared/types';
import { BulkAttributeConfig, BulkAttributeValue } from '../../../shared/components/bulkUpdate';
import { getApiBaseUrl } from '../../../shared/utils/apiBase';
import { normalizeDateToUTC } from '../../../shared/utils/dateUtils';

type BulkUpdateInput = {
  attrInternalName: string;
  value: BulkAttributeValue;
};

type CommitBulkUpdateNpiSteeringParams = {
  screenGridId: number;
  schemaName: string;
  id1List: number[];
  updates: BulkUpdateInput[];
  attributes: BulkAttributeConfig[];
  updatedBy: number | string;
  bulkUpdateMaxObjectsSize: number;
};

function formatBulkValueByType(value: BulkAttributeValue, attrDataType: string) {
  if (value === null) return null;

  if (value instanceof Date) {
    return normalizeDateToUTC(value)?.toISOString().slice(0, 10) ?? null;
  }

  if (attrDataType === 'number') {
    const parsed = Number(value);
    return Number.isNaN(parsed) ? value : parsed;
  }

  return value;
}

/**
 * Commits validated Bulk Update changes for NPI Steering rows to the database.
 *
 * Builds a single `sheet1` update payload for all filtered `id1` rows, normalizes
 * values by attribute data type, and sends the request to `/api/postgres/bulkUpdate`.
 *
 * @param params Commit context including target grid/schema, row ids, typed updates,
 * attribute metadata, and current user id.
 * @returns `{ success, errors, result }` where `success=false` when backend returns errors.
 */
export async function commitBulkUpdateNpiSteeringService({
  screenGridId,
  schemaName,
  id1List,
  updates,
  attributes,
  updatedBy,
  bulkUpdateMaxObjectsSize,
}: CommitBulkUpdateNpiSteeringParams) {
  // Client-Side Guardrail Validation
  // Enforce limit on client side to catch massive payloads before sending over network

  if (id1List.length > bulkUpdateMaxObjectsSize) {
    return {
      success: false,
      errors: [
        {
          message: `You can update a maximum of ${bulkUpdateMaxObjectsSize} records at a time. Please reduce your selection`,
        },
      ],
      result: null,
    };
  }
  const attrTypeMap = new Map(attributes.map((attr) => [attr.attrInternalName, attr.attrDataType]));

  const updateSet = updates.reduce<Record<string, unknown>>((acc, item) => {
    const attrType = attrTypeMap.get(item.attrInternalName) ?? 'string';
    acc[item.attrInternalName] = formatBulkValueByType(item.value, attrType);
    return acc;
  }, {});

  updateSet.updated_by = updatedBy;
  updateSet.update_date = new Date().toISOString().substring(0, 19);

  const sheet1UpdateObjects = id1List.map((id1) => ({
    where: {
      id1: {
        _eq: id1,
      },
    },
    _set: updateSet,
  }));

  const sheet1Payload = {
    deleteIds: [],
    insertObjects: [],
    pkey: 'id1',
    schema: schemaName,
    table: 'sheet1',
    updateObjects: sheet1UpdateObjects,
  };

  const apiBase = getApiBaseUrl();
  const response = await fetch(`${apiBase}/api/postgres/bulkUpdate`, {
    method: 'PATCH',
    headers: new Headers({
      'Content-Type': 'application/json',
      Accept: 'application/json',
    }),
    body: JSON.stringify({
      screenGridId,
      tables: [sheet1Payload],
    } as JSONValue),
  });

  const result = await response.json();

  if (!response.ok) {
    return {
      success: false,
      errors: result?.errors ?? [result?.message ?? 'Bulk update request failed'],
      result,
    };
  }

  if (result?.errors?.length) {
    return {
      success: false,
      errors: result.errors,
      result,
    };
  }

  return {
    success: true,
    errors: [],
    result,
  };
}
